import { inject, Injectable, OnDestroy } from '@angular/core';
import * as LDClient from 'launchdarkly-js-client-sdk';
import {
  BehaviorSubject,
  catchError,
  filter,
  mergeMap,
  Observable,
  of,
  Subject,
  switchMap,
} from 'rxjs';

import { RuntimeConfigService } from './services/runtime-config';

export enum EFeatureFlag {
  RECAPTCHA_ENABLED = 'enabled-recaptcha',
  CHATBOT_WIDGET_ENABLED = 'enabled-chatbot-widget',
}

/**
 * LaunchDarkly client for runtime feature toggles. Initialized at app bootstrap
 * via `initializeFeatureFlagService`; exposes `isEnabled` and `watchEnabledFlag`
 * as Observables that wait until the LD client is ready.
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService implements OnDestroy {
  private readonly runtimeConfigService: RuntimeConfigService =
    inject(RuntimeConfigService);

  private readonly flagsSubject = new Subject<string>();
  private readonly initialized = new BehaviorSubject<boolean>(false);

  private ldClient!: LDClient.LDClient;

  async initializeClient() {
    const defaultContext = { anonymous: true };
    if (
      !this.ldClient &&
      this.runtimeConfigService.prequalConfig['LAUNCH_DARKLY_CLIENT_ID']
    ) {
      this.ldClient = LDClient.initialize(
        this.runtimeConfigService.prequalConfig['LAUNCH_DARKLY_CLIENT_ID'],
        defaultContext,
        {
          streaming: true,
        }
      );

      try {
        await this.ldClient.waitForInitialization();
        this.initialized.next(true);
        console.log('LaunchDarkly client initialized successfully.');

        this.ldClient.on('change', (changes) => {
          Object.keys(changes).forEach((flagKey) => {
            this.flagsSubject.next(flagKey);
          });
        });
      } catch (err) {
        console.error('LaunchDarkly client failed to initialize:', err);
      }
    }
  }

  watchEnabledFlag(
    flagName: string,
    defaultValue = false
  ): Observable<boolean> {
    return this.flagsSubject.pipe(
      filter((key) => key === flagName),
      mergeMap(() => this.isEnabled(flagName, defaultValue))
    );
  }

  isEnabled(flagName: string, defaultValue = false): Observable<boolean> {
    return this.initialized.asObservable().pipe(
      filter((isInitalized) => isInitalized),
      switchMap(() =>
        of(this.ldClient.variation(flagName, defaultValue) as boolean)
      ),
      catchError((error) => {
        console.error(`Error evaluating feature flag '${flagName}':`, error);
        return of(false);
      })
    );
  }

  ngOnDestroy() {
    if (this.ldClient) {
      this.ldClient.close();
    }
    this.flagsSubject.complete();
  }
}

export function initializeFeatureFlagService(
  featureFlagService: FeatureFlagService
): () => Promise<void> {
  return () => featureFlagService.initializeClient();
}
